// External/third-party imports
import React from "react";
import PropTypes from "prop-types";
import { IconButton, Tooltip } from "@toddle-edu/ds-web";
import { DeleteOutlined, PencilOutlined } from "@toddle-edu/ds-icons";

// Internal absolute imports
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { isNewVideoMeetingUIFeatureEnabled } from "MyCalendar/components/EventModalV2/modules/Utils";

// Relative imports
import classes from "../EventHeader.scss";

const styles = {
  iconButtonCustomStyles: { height: "fit-content", padding: "8px" },
};

const DisabledTooltip = ({ type, isGradingPeriodUnlocked, t, children }) => {
  let tooltip = "";
  if (!isGradingPeriodUnlocked) {
    tooltip = t(
      "academicSetup:you_cannot_make_changes_to_assignment_since_locked"
    );
  } else if (type === "delete") {
    tooltip = t("course:locked_item_delete_restriction_tooltip", {
      entity: t("common:assignment"),
    });
  } else {
    tooltip = t("course:locked_item_edit_restriction_tooltip", {
      entity: t("common:assignment"),
    });
  }

  return (
    <Tooltip
      tooltip={<div className={classes.tooltipContainer}>{tooltip}</div>}
    >
      {children}
    </Tooltip>
  );
};

DisabledTooltip.propTypes = {
  type: PropTypes.oneOf(["edit", "delete"]).isRequired,
  isGradingPeriodUnlocked: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

const ActionButton = ({
  type,
  onClick,
  disabled,
  testId,
  isGradingPeriodUnlocked,
  isMeetingDetailsLoading,
  t,
}) => {
  const iconActionMap = {
    edit: <PencilOutlined variant={"subtle"} />,
    delete: <DeleteOutlined variant={"subtle"} />,
  };

  const showDisabledTooltip = disabled && !isMeetingDetailsLoading;

  return (
    <ConditionalWrapper
      condition={showDisabledTooltip}
      wrapper={
        <DisabledTooltip
          type={type}
          isGradingPeriodUnlocked={isGradingPeriodUnlocked}
          t={t}
        />
      }
      type={"wrapperAsElement"}
    >
      <IconButton
        icon={iconActionMap[type]}
        style={styles.iconButtonCustomStyles}
        onClick={onClick}
        variant={"neutral"}
        type={"plain"}
        size={"medium"}
        isCompact={true}
        testId={`${testId}-${type}`}
        disabled={disabled}
        dsVersion={"2.0"}
      />
    </ConditionalWrapper>
  );
};

ActionButton.propTypes = {
  type: PropTypes.oneOf(["edit", "delete"]).isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  testId: PropTypes.string,
  isGradingPeriodUnlocked: PropTypes.bool.isRequired,
  isMeetingDetailsLoading: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

ActionButton.defaultProps = {
  disabled: false,
  testId: "",
  isMeetingDetailsLoading: false,
};

const ActionButtons = ({
  onClickEdit,
  onClickDelete,
  hideEditButton,
  isViewOnlyTag,
  isGradingPeriodUnlocked,
  isAssessmentLocked,
  canManageAssignment,
  userType,
  isNotUserCourse,
  isCurrentAcademicYear,
  t,
  testId,
  isVideoMeeting,
  canUserEditThisMeeting,
  isMeetingDetailsLoading,
}) => {
  const disableEditDeleteButtons =
    !isGradingPeriodUnlocked || isAssessmentLocked;

  const isVideoMeetingRevampEnabled = isNewVideoMeetingUIFeatureEnabled();

  const canEditMeeting = canUserEditThisMeeting
    ? canUserEditThisMeeting()
    : false;

  // Handle video meeting edit/delete buttons
  if (isVideoMeeting && isVideoMeetingRevampEnabled && canEditMeeting) {
    return (
      <>
        <ActionButton
          type="edit"
          onClick={onClickEdit}
          disabled={isMeetingDetailsLoading}
          testId={testId}
          isGradingPeriodUnlocked={isGradingPeriodUnlocked}
          isMeetingDetailsLoading={isMeetingDetailsLoading}
          t={t}
        />
        <ActionButton
          type="delete"
          onClick={onClickDelete}
          disabled={isMeetingDetailsLoading}
          testId={testId}
          isGradingPeriodUnlocked={isGradingPeriodUnlocked}
          isMeetingDetailsLoading={isMeetingDetailsLoading}
          t={t}
        />
      </>
    );
  }

  // For old video meetings and assignments, the original logic applies
  /**
   * Only staff users can see the edit/delete buttons for assignments
   * in their own courses and the current academic year.
   */
  return userType === "staff" && !isNotUserCourse && isCurrentAcademicYear ? (
    <>
      {/* Can edit when it is not a view only tag and hideEditButton, canManageAssignment is true from props */}
      {!(hideEditButton || isViewOnlyTag) && canManageAssignment ? (
        <ActionButton
          type="edit"
          onClick={onClickEdit}
          disabled={disableEditDeleteButtons}
          testId={testId}
          isGradingPeriodUnlocked={isGradingPeriodUnlocked}
          t={t}
        />
      ) : null}
      {/* Can delete when it is not a view only tag and canManageAssignment is true from props */}
      {!isViewOnlyTag && canManageAssignment ? (
        <ActionButton
          type="delete"
          onClick={onClickDelete}
          disabled={disableEditDeleteButtons}
          testId={testId}
          isGradingPeriodUnlocked={isGradingPeriodUnlocked}
          t={t}
        />
      ) : null}
    </>
  ) : null;
};

ActionButtons.propTypes = {
  onClickEdit: PropTypes.func.isRequired,
  onClickDelete: PropTypes.func.isRequired,
  hideEditButton: PropTypes.bool,
  isViewOnlyTag: PropTypes.bool,
  isGradingPeriodUnlocked: PropTypes.bool,
  isAssessmentLocked: PropTypes.bool,
  canManageAssignment: PropTypes.bool,
  userType: PropTypes.string,
  isNotUserCourse: PropTypes.bool,
  isCurrentAcademicYear: PropTypes.bool,
  t: PropTypes.func.isRequired,
  testId: PropTypes.string,
  isVideoMeeting: PropTypes.bool,
  isMeetingDetailsLoading: PropTypes.bool,
};

ActionButtons.defaultProps = {
  hideEditButton: false,
  isViewOnlyTag: false,
  isGradingPeriodUnlocked: true,
  isAssessmentLocked: false,
  canManageAssignment: true,
  userType: "",
  isNotUserCourse: false,
  isCurrentAcademicYear: true,
  testId: "",
  isVideoMeeting: false,
  isMeetingDetailsLoading: false,
};

export default ActionButtons;
